import { useEffect, useState, type ReactNode } from 'react';
import { isValidEmailOtp, normalizeEmailOtp } from '@/lib/auth-otp';
import { isStandalonePwa } from '@/lib/pwa';

interface Props {
  email: string;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onChangeEmail?: () => void;
  headline?: string;
  subline?: ReactNode;
}

const RESEND_COOLDOWN_SEC = 60;

export function EmailOtpVerify({
  email,
  onVerify,
  onResend,
  onChangeEmail,
  headline = 'Enter your sign-in code',
  subline,
}: Props) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SEC);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setInterval(() => {
      setCooldown((c) => Math.max(0, c - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [cooldown]);

  async function submit(nextCode?: string) {
    const raw = nextCode ?? code;
    if (!isValidEmailOtp(raw)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    const token = normalizeEmailOtp(raw);
    setError(null);
    setSubmitting(true);
    try {
      await onVerify(token);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (cooldown > 0) return;
    setError(null);
    setResending(true);
    try {
      await onResend();
      setCode('');
      setCooldown(RESEND_COOLDOWN_SEC);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setResending(false);
    }
  }

  const defaultSubline = subline ?? (
    <>We sent a 6-digit code to <strong>{email}</strong>.</>
  );

  return (
    <div className="space-y-4 text-center">
      <div className="font-serif text-2xl font-bold text-navy">{headline}</div>
      <p className="text-sm text-slate-600">{defaultSubline}</p>

      {isStandalonePwa() && (
        <p className="rounded-xl bg-cream px-3 py-2 text-left text-xs leading-relaxed text-ink-soft">
          You&apos;re in the installed app — type the code here. You don&apos;t need to tap the email link.
        </p>
      )}

      <div>
        <label className="mb-2 block text-left text-sm font-semibold text-slate-700">6-digit code</label>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          value={code}
          onChange={(e) => {
            const next = normalizeEmailOtp(e.target.value);
            setCode(next);
            setError(null);
            if (next.length === 6) void submit(next);
          }}
          placeholder="000000"
          className="w-full rounded-xl border-2 border-line bg-paper px-4 py-3 text-center font-mono text-2xl tracking-[0.35em] focus:border-gold focus:outline-none"
        />
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 p-3 text-left text-sm text-red-700">{error}</div>
      )}

      <button
        type="button"
        disabled={submitting || !isValidEmailOtp(code)}
        onClick={() => void submit()}
        className="w-full rounded-xl bg-gold py-3 text-sm font-bold tracking-wider text-navy disabled:opacity-50"
      >
        {submitting ? 'VERIFYING…' : 'SIGN IN'}
      </button>

      <div className="flex flex-col gap-2 text-sm">
        <button
          type="button"
          disabled={resending || cooldown > 0}
          onClick={() => void handleResend()}
          className="text-slate-600 underline disabled:no-underline disabled:opacity-50"
        >
          {resending
            ? 'Sending…'
            : cooldown > 0
              ? `Resend code in ${cooldown}s`
              : 'Resend code'}
        </button>
        {onChangeEmail && (
          <button type="button" onClick={onChangeEmail} className="text-slate-500 underline">
            Use a different email
          </button>
        )}
      </div>

      <p className="text-xs text-slate-400">
        Prefer the link? You can still tap the sign-in link in your email — we&apos;ll detect it automatically.
      </p>
    </div>
  );
}
